import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { ListTreeIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Tooltip } from '@air/primitive-tooltip';
import { resetSelectedItemsAction } from '@air/redux-selected-items';

import { GalleryItemType } from '~/components/Gallery/types';
import { useShowExpandNestedContentToggle } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useShowExpandNestedContentToggle';
import { GalleryNestedItemsChip } from '~/components/v2/components/shared/gallery/GalleryNestedItemsChip';
import { useToggleNestedItems } from '~/hooks/search/useToggleNestedItems';

export type GalleryNestedSectionHeaderControlsProps = {
  /** Share links can lock nested content. Private boards pass false. */
  isDisabled: boolean;
  isFirstSection: boolean;
  /**
   * A single gallery's first section is the chip host. Showcase listing blocks are separate
   * galleries, so only the first one that still has items should pass true.
   */
  isNestedChipHost: boolean;
  itemCount: number;
  sectionType: GalleryItemType;
};

export const GalleryNestedSectionHeaderControls = memo(
  ({
    isDisabled,
    isFirstSection,
    isNestedChipHost,
    itemCount,
    sectionType,
  }: GalleryNestedSectionHeaderControlsProps) => {
    const dispatch = useDispatch();
    const { showExpandNestedContentToggle } = useShowExpandNestedContentToggle({ isDisabled });
    const { isFlattenedView, toggleNestedItems } = useToggleNestedItems();
    const canShowNestedItemsChip = showExpandNestedContentToggle && isFlattenedView && itemCount > 0;

    // Selected boards disappear from the gallery once nested items are shown, so a stale selection would linger.
    const handleExpandNestedItems = useCallback(() => {
      dispatch(resetSelectedItemsAction());
      toggleNestedItems();
    }, [dispatch, toggleNestedItems]);

    const hasSubboards = itemCount > 0;
    const shouldShowExpandButton =
      showExpandNestedContentToggle && !isFlattenedView && hasSubboards && sectionType === GalleryItemType.board;
    // The boards section is hidden while flattened, so the chip rides on whichever header renders first.
    const shouldShowNestedItemsChip = canShowNestedItemsChip && isFirstSection && isNestedChipHost;

    if (shouldShowExpandButton) {
      return (
        <div className="flex items-center gap-2 pl-2">
          <span aria-hidden className="h-4 w-px shrink-0 bg-gray-6" />
          <Tooltip label="Reveal all items in subboards" side="top">
            <Button
              appearance="ghost"
              color="gray"
              data-testid="BOARD_GALLERY_EXPAND_NESTED_BUTTON"
              onClick={handleExpandNestedItems}
              prefix={<ListTreeIcon size="xs" />}
              size="small"
            >
              Expand
            </Button>
          </Tooltip>
        </div>
      );
    }

    if (shouldShowNestedItemsChip) {
      return (
        <div className="pl-2">
          <GalleryNestedItemsChip />
        </div>
      );
    }

    return null;
  },
);

GalleryNestedSectionHeaderControls.displayName = 'GalleryNestedSectionHeaderControls';
